import React, { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { 
  Wifi, 
  WifiOff, 
  Search, 
  RefreshCw,
  Settings,
  Play,
  Save,
  Trash2,
  CheckCircle,
  XCircle,
  ArrowLeft
} from 'lucide-react'

const ConnectDevicePanel = ({
  onDeviceConnect,
  onBack,
  savedDevices = [],
  onSaveDevice,
  onRemoveDevice
}) => {
  const [isConnecting, setIsConnecting] = useState(false)
  const [devices, setDevices] = useState([])
  const [showManualEntry, setShowManualEntry] = useState(false)
  const [manualIP, setManualIP] = useState('')
  const [manualPort, setManualPort] = useState('80')
  const [connectionError, setConnectionError] = useState(null)

  const testDeviceConnection = async (ip, port) => {
    try {
      console.log(`🔍 Testing ${ip}:${port}...`)
      
      // Test multiple endpoints that your NodeMCU provides
      const endpoints = ['/status', '/discovery', '/test']
      
      for (const endpoint of endpoints) {
        try {
          console.log(`  Trying ${ip}:${port}${endpoint}...`)
          
          const response = await fetch(`http://${ip}:${port}${endpoint}`, {
            method: 'GET',
            mode: 'cors', // Try CORS first
            headers: {
              'Accept': 'application/json',
              'Content-Type': 'application/json'
            },
            signal: AbortSignal.timeout(3000) // 3 second timeout
          })

          if (response.ok) {
            const deviceData = await response.json()
            console.log(`✅ Response from ${ip}:${port}${endpoint}:`, deviceData)
            
            // Check if this is your NodeMCU device
            if (deviceData.name === 'EZ Control Device' || 
                deviceData.device === 'nodemcu' || 
                deviceData.ssid === 'EZ_Control_AP' ||
                deviceData.hotspot_ssid === 'EZ_Control_AP' ||
                deviceData.name || // Accept any device with a name
                deviceData.device) { // Accept any device with device type
              
              console.log(`🎯 Found device at ${ip}:${port}`)
              
              return {
                ip: ip,
                port: port,
                name: deviceData.name || 'EZ Control Device',
                version: deviceData.version || '5.0.0',
                mode: deviceData.mode || 'wifi',
                ssid: deviceData.ssid || deviceData.hotspot_ssid || 'Unknown',
                rssi: deviceData.rssi || 0,
                mac: deviceData.mac || 'Unknown',
                status: 'available',
                pinStates: deviceData.pinStates || {},
                deviceType: 'nodemcu',
                connectionMode: deviceData.mode || 'wifi'
              }
            }
          }
        } catch (endpointError) {
          console.log(`  ❌ ${ip}:${port}${endpoint} failed:`, endpointError.message)
          
          // If CORS fails, try without CORS mode
          if (endpointError.message.includes('CORS') || endpointError.message.includes('Failed to fetch')) {
            try {
              console.log(`  🔄 Retrying ${ip}:${port}${endpoint} without CORS...`)
              
              const response = await fetch(`http://${ip}:${port}${endpoint}`, {
                method: 'GET',
                mode: 'no-cors', // Try without CORS
                signal: AbortSignal.timeout(3000)
              })
              
              // With no-cors, we can't read the response, but we can check if the request succeeded
              if (response.type === 'opaque') {
                console.log(`✅ Device responding at ${ip}:${port}${endpoint} (no-cors mode)`)
                
                // Since we can't read the response in no-cors mode, create a basic device object
                return {
                  ip: ip,
                  port: port,
                  name: 'EZ Control Device',
                  version: '5.0.0',
                  mode: 'wifi',
                  ssid: 'Unknown',
                  rssi: 0,
                  mac: 'Unknown',
                  status: 'available',
                  pinStates: {},
                  deviceType: 'nodemcu',
                  connectionMode: 'wifi'
                }
              }
            } catch (noCorsError) {
              console.log(`  ❌ No-cors also failed:`, noCorsError.message)
            }
          }
          
          // Endpoint not available, try next one
          continue
        }
      }
      
      // If all endpoints fail, try a simple ping test
      try {
        console.log(`  🔄 Trying simple ping test to ${ip}:${port}...`)
        
        const response = await fetch(`http://${ip}:${port}/`, {
          method: 'GET',
          mode: 'no-cors',
          signal: AbortSignal.timeout(2000)
        })
        
        if (response.type === 'opaque') {
          console.log(`✅ Device responding at ${ip}:${port} (ping test)`)
          
          // Create a basic device object for responding devices
          return {
            ip: ip,
            port: port,
            name: 'EZ Control Device',
            version: '5.0.0',
            mode: 'wifi',
            ssid: 'Unknown',
            rssi: 0,
            mac: 'Unknown',
            status: 'available',
            pinStates: {},
            deviceType: 'nodemcu',
            connectionMode: 'wifi'
          }
        }
      } catch (pingError) {
        console.log(`  ❌ Ping test failed:`, pingError.message)
      }
      
      return null
    } catch (error) {
      console.log(`❌ Connection failed to ${ip}:${port}:`, error.message)
      return null
    }
  }

  const handleManualConnect = async () => {
    if (manualIP.trim()) {
      try {
        setIsConnecting(true)
        setConnectionError(null)
        
        console.log('🔧 Attempting manual connection to:', manualIP, 'port:', manualPort)
        
        const device = await testDeviceConnection(manualIP.trim(), parseInt(manualPort) || 80)
        
        if (device) {
          setDevices([device])
          setShowManualEntry(false)
          setManualIP('')
          console.log('✅ Manual connection successful:', device)
        } else {
          setConnectionError(`Device not found at ${manualIP}:${manualPort}. 
          
Possible solutions:
• Check if NodeMCU is powered on and running
• Verify the IP address is correct
• Make sure you're on the same network as the device
• Try accessing http://${manualIP}:${manualPort} in your browser first`)
        }
      } catch (error) {
        console.error('Manual connection error:', error)
        setConnectionError(`Connection failed: ${error.message}. 
        
Try these steps:
1. Open http://${manualIP}:${manualPort} in your browser
2. If it works in browser but not here, it's a CORS issue
3. Make sure your NodeMCU has CORS enabled`)
      } finally {
        setIsConnecting(false)
      }
    }
  }

  const handleQuickScan = async () => {
    try {
      setIsConnecting(true)
      setConnectionError(null)
      setDevices([])
      
      console.log('🚀 Starting quick scan for common EZ Control IPs...')
      
      // Common IPs where EZ Control devices might be found
      const commonIPs = [
        '192.168.1.4',    // Common hotspot IP
        '192.168.4.1',    // ESP8266 default AP IP
        '192.168.1.1',    // Router IP (if device is connected)
        '192.168.0.100',  // Common DHCP range
        '192.168.1.100'   // Common DHCP range
      ]

      const foundDevices = []

      for (const ip of commonIPs) {
        console.log(`📡 Testing ${ip}...`)
        const device = await testDeviceConnection(ip, 80)
        
        if (device) {
          foundDevices.push(device)
          console.log(`✅ Found device at ${ip}`)
        }
      }
      
      setDevices(foundDevices)
      console.log(`📊 Quick scan completed: Found ${foundDevices.length} devices`)
      
      if (foundDevices.length === 0) {
        setConnectionError('No EZ Control devices found. Try manual entry with your device IP.')
      }
      
    } catch (error) {
      setConnectionError(`Quick scan failed: ${error.message}`)
    } finally {
      setIsConnecting(false)
    }
  }

  const handleDeviceSelect = async (device) => {
    try {
      // Test connection before connecting
      const testResult = await testDeviceConnection(device.ip, device.port)
      if (testResult) {
        // Save to local storage
        const deviceToSave = {
          ...device,
          lastConnected: new Date().toISOString()
        }
        onSaveDevice(deviceToSave)
        
        // Connect to device
        onDeviceConnect(device)
      } else {
        setConnectionError('Device is no longer responding. Please scan again.')
      }
    } catch (error) {
      setConnectionError(`Connection failed: ${error.message}`)
    }
  }

  const handleQuickConnect = async (savedDevice) => {
    try {
      const testResult = await testDeviceConnection(savedDevice.ip, savedDevice.port)
      if (testResult) {
        onDeviceConnect(savedDevice)
      } else {
        setConnectionError(`Saved device ${savedDevice.name} is not responding.`)
      }
    } catch (error) {
      setConnectionError(`Quick connect failed: ${error.message}`)
    }
  }

  const handleTestConnection = async () => {
    if (manualIP.trim()) {
      try {
        setIsConnecting(true)
        setConnectionError(null)
        
        console.log('🧪 Testing connection to:', manualIP, 'port:', manualPort)
        
        // Try to open the device URL in a new tab for manual testing
        const deviceUrl = `http://${manualIP.trim()}:${manualPort}`
        window.open(deviceUrl, '_blank')
        
        const device = await testDeviceConnection(manualIP.trim(), parseInt(manualPort) || 80)
        
        if (device) {
          setConnectionError(`✅ Connection test successful! Device found: ${device.name}
          
Device details:
• IP: ${device.ip}:${device.port}
• Name: ${device.name}
• Version: ${device.version}
• Mode: ${device.mode}

You can now connect to this device.`)
        } else {
          setConnectionError(`❌ Connection test failed for ${manualIP}:${manualPort}

Debugging steps:
1. Check if the browser tab opened with the device page
2. If the page loads in browser but test fails, it's a CORS issue
3. Make sure NodeMCU is running the latest firmware
4. Verify you're on the same network`)
        }
      } catch (error) {
        console.error('Test connection error:', error)
        setConnectionError(`Test failed: ${error.message}

Try opening http://${manualIP}:${manualPort} directly in your browser.`)
      } finally {
        setIsConnecting(false)
      }
    }
  }

  const getSignalStrength = (rssi) => {
    if (rssi >= -50) return { bars: 4, color: 'text-green-400' }
    if (rssi >= -60) return { bars: 3, color: 'text-yellow-400' }
    if (rssi >= -70) return { bars: 2, color: 'text-orange-400' }
    return { bars: 1, color: 'text-red-400' }
  }

  const renderSignalBars = (rssi) => {
    const { bars, color } = getSignalStrength(rssi)
    return (
      <div className="flex space-x-1">
        {[1, 2, 3, 4].map((bar) => (
          <div
            key={bar}
            className={`w-1 h-${bar} rounded-full ${
              bar <= bars ? color : 'bg-gray-600'
            }`}
          />
        ))}
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 text-white p-6">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ y: -50, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          className="text-center mb-8"
        >
          <h1 className="text-3xl font-bold mb-2">Connect to EZ Control Device</h1>
          <p className="text-gray-400">Enter your NodeMCU device IP address to connect</p>
        </motion.div>

        {/* Saved Devices */}
        {savedDevices.length > 0 && (
          <motion.div
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.1 }}
            className="card p-6 mb-6"
          >
            <h2 className="text-xl font-semibold mb-4">Saved Devices</h2>
            <div className="space-y-3">
              {savedDevices.map((device, index) => (
                <div
                  key={`${device.ip}-${index}`}
                  className="flex items-center justify-between p-3 bg-gray-800/50 rounded-lg"
                >
                  <div className="flex items-center space-x-3">
                    <Wifi className="w-5 h-5 text-primary-400" />
                    <div>
                      <div className="font-medium">{device.name}</div>
                      <div className="text-sm text-gray-400">
                        {device.ip}:{device.port}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => handleQuickConnect(device)}
                      className="btn-primary text-sm"
                    >
                      Connect
                    </button>
                    <button
                      onClick={() => onRemoveDevice(device)}
                      className="btn-danger text-sm"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </motion.div>
        )}

        {/* Connection Options */}
        <motion.div
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.2 }}
          className="card p-6 mb-6"
        >
          <h2 className="text-xl font-semibold mb-4">Connect to Device</h2>

          <div className="flex items-center space-x-3 mb-4">
            <button
              onClick={handleQuickScan}
              disabled={isConnecting}
              className="btn-primary flex items-center space-x-2"
            >
              <Play className="w-4 h-4" />
              <span>{isConnecting ? 'Scanning...' : 'Quick Scan'}</span>
            </button>
            
            <button
              onClick={() => setShowManualEntry(!showManualEntry)}
              className="btn-secondary flex items-center space-x-2"
            >
              <Settings className="w-4 h-4" />
              <span>Manual Entry</span>
            </button>
          </div>

          {/* Manual Entry */}
          <AnimatePresence>
            {showManualEntry && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                className="p-4 bg-gray-800/50 rounded-lg"
              >
                <h4 className="text-sm font-medium text-gray-300 mb-3">Enter Device IP Address</h4>
                <div className="grid grid-cols-2 gap-4 mb-3">
                  <div>
                    <label className="block text-sm text-gray-400 mb-1">Device IP Address</label>
                    <input
                      type="text"
                      value={manualIP}
                      onChange={(e) => setManualIP(e.target.value)}
                      placeholder="192.168.1.100"
                      className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-primary-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-gray-400 mb-1">Port</label>
                    <input
                      type="number"
                      value={manualPort}
                      onChange={(e) => setManualPort(e.target.value)}
                      placeholder="80"
                      className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-primary-500"
                    />
                  </div>
                </div>
                
                <div className="flex space-x-2">
                  <button
                    onClick={handleManualConnect}
                    disabled={!manualIP.trim() || isConnecting}
                    className="btn-primary flex-1"
                  >
                    {isConnecting ? 'Connecting...' : 'Connect'}
                  </button>
                  <button
                    onClick={handleTestConnection}
                    disabled={!manualIP.trim() || isConnecting}
                    className="btn-secondary"
                  >
                    Test Connection
                  </button>
                  <button
                    onClick={() => setShowManualEntry(false)}
                    className="btn-secondary"
                  >
                    Cancel
                  </button>
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Error Message */}
          {connectionError && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="p-3 bg-red-500/20 border border-red-500/50 rounded-lg text-red-300"
            >
              <div className="flex items-center space-x-2">
                <XCircle className="w-4 h-4" />
                <span>{connectionError}</span>
              </div>
            </motion.div>
          )}
        </motion.div>

        {/* Device List */}
        <motion.div
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.3 }}
          className="card p-6"
        >
          <h2 className="text-xl font-semibold mb-4">Found Devices</h2>
          
          {devices.length === 0 ? (
            <div className="text-center py-8">
              {isConnecting ? (
                <div>
                  <motion.div
                    animate={{ rotate: 360 }}
                    transition={{ duration: 2, repeat: Infinity, ease: 'linear' }}
                    className="w-12 h-12 border-2 border-primary-500 border-t-transparent rounded-full mx-auto mb-4"
                  />
                  <p className="text-gray-400">Scanning for EZ Control devices...</p>
                </div>
              ) : (
                <div>
                  <Search className="w-12 h-12 text-gray-500 mx-auto mb-4" />
                  <p className="text-gray-400">No devices found</p>
                  <p className="text-sm text-gray-500 mt-2">
                    Use "Quick Scan" or "Manual Entry" to find your device
                  </p>
                </div>
              )}
            </div>
          ) : (
            <div className="space-y-3">
              {devices.map((device, index) => (
                <motion.div
                  key={`${device.ip}-${index}`}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  className="p-4 bg-gray-800/50 rounded-lg border border-gray-700 hover:border-primary-500/50 transition-all cursor-pointer"
                  onClick={() => handleDeviceSelect(device)}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <Wifi className="w-5 h-5 text-primary-400" />
                      <div>
                        <div className="font-medium text-white">{device.name}</div>
                        <div className="text-sm text-gray-400">
                          {device.ip}:{device.port} • {device.mode}
                        </div>
                        <div className="flex items-center space-x-4 mt-1 text-xs text-gray-500">
                          <span>SSID: {device.ssid}</span>
                          {device.version && <span>v{device.version}</span>}
                        </div>
                      </div>
                    </div>
                    
                    <div className="flex items-center space-x-3">
                      {device.rssi && renderSignalBars(device.rssi)}
                      <CheckCircle className="w-5 h-5 text-green-400" />
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </motion.div>

        {/* Back Button */}
        <motion.div
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.4 }}
          className="mt-6 text-center"
        >
          <button
            onClick={onBack}
            className="btn-secondary flex items-center space-x-2 mx-auto"
          >
            <ArrowLeft className="w-4 h-4" />
            <span>Back to Control Panel</span>
          </button>
        </motion.div>
      </div>
    </div>
  )
}

export default ConnectDevicePanel 